<template>
  <div>
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <span class="label-tag">Portfolio</span>
        <h1>Selected Work</h1>
        <p class="page-header-sub">
          A selection of analysis, reports, and tools demonstrating
          the breadth of my work across football analytics.
        </p>
      </div>
    </section>

    <!-- Portfolio Categories -->
    <section class="section section-alt">
      <div class="container">
        <div class="row g-5">
          <div class="col-lg-6" v-for="category in categories" :key="category.title">
            <div class="portfolio-card">
              <div class="portfolio-card-header">
                <span class="label-tag">{{ category.tag }}</span>
                <h3>{{ category.title }}</h3>
              </div>
              <p class="portfolio-description">{{ category.description }}</p>
              <ul class="portfolio-highlights">
                <li v-for="highlight in category.highlights" :key="highlight">
                  {{ highlight }}
                </li>
              </ul>
              <div class="portfolio-levels" v-if="category.levels">
                <span class="level-badge" v-for="level in category.levels" :key="level">
                  {{ level }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Data Platforms -->
    <section class="section section-dark">
      <div class="container">
        <div class="text-center mb-5">
          <span class="label-tag">Tools & Platforms</span>
          <h2>Data Sources & Technologies</h2>
        </div>
        <div class="row g-4">
          <div class="col-6 col-md-3" v-for="platform in platforms" :key="platform.name">
            <div class="platform-card text-center">
              <div class="platform-name">{{ platform.name }}</div>
              <div class="platform-type">{{ platform.type }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="section section-alt">
      <div class="container text-center">
        <h2>Interested in Seeing More?</h2>
        <p class="section-subtitle mx-auto mt-3">
          I'm happy to walk through specific examples and discuss
          how similar work could benefit your organisation.
        </p>
        <router-link to="/contact" class="btn-primary-custom">Request Samples</router-link>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'PortfolioView',
  data() {
    return {
      categories: [
        {
          tag: 'Strategy',
          title: 'Team Value Management & Competitive Strategy',
          description: 'Evidence-based analysis of team value management metrics to identify competitive advantages and disadvantages, forming the foundation for strategic principles and targeted projects that strengthen what works and address what doesn\'t.',
          highlights: [
            'Team value management metric analysis using evidence-based frameworks',
            'Competitive advantage and disadvantage identification',
            'Strategic principle development aligned to performance data',
            'Project design to increase advantages and relieve disadvantages',
            'Executive-ready strategic recommendations for club leadership'
          ],
          levels: null
        },
        {
          tag: 'Analysis',
          title: 'Match & Tactical Analysis',
          description: 'Detailed tactical breakdowns of individual matches, covering team shape, pressing patterns, transitions, and key moments that decide outcomes.',
          highlights: [
            'Formation and shape analysis with visual mapping',
            'Pressing trigger identification and effectiveness metrics',
            'Transition analysis (attack-to-defence and defence-to-attack)',
            'Key moment breakdowns with video timestamps'
          ],
          levels: null
        },
        {
          tag: 'Reports',
          title: 'Opposition Analysis Reports',
          description: 'Pre-match opposition reports delivered to coaching staff, covering tactical tendencies, key players, set-piece routines, and strategic recommendations.',
          highlights: [
            'Tactical tendencies and preferred formations',
            'Key player profiles and threat assessment',
            'Set-piece analysis (attacking and defensive)',
            'Actionable recommendations for match preparation'
          ],
          levels: ['EFL League 1', 'EFL League 2', 'National League', 'MLS']
        },
        {
          tag: 'Data',
          title: 'Data Visualisation & Analytics',
          description: 'Statistical analysis and custom visualisations that transform complex datasets into clear, decision-ready insight for technical staff and executives.',
          highlights: [
            'Pass network and possession chain analysis',
            'Expected goals (xG) and shot quality mapping',
            'Player performance radar charts and comparisons',
            'GPS and physical output analysis'
          ],
          levels: null
        },
        {
          tag: 'Software',
          title: 'Custom Analytics Tools',
          description: 'Bespoke software tools built to streamline analysis workflows, from code windows for live event tagging to AI-assisted player tracking systems.',
          highlights: [
            'Custom code windows for live match tagging',
            'AI-powered player tracking and position mapping',
            'Automated report generation systems',
            'Interactive pitch visualisation dashboards'
          ],
          levels: null
        }
      ],
      platforms: [
        { name: 'StatsBomb', type: 'Event Data' },
        { name: 'Wyscout', type: 'Video & Data' },
        { name: 'GPS Systems', type: 'Tracking Data' },
        { name: 'Custom Tools', type: 'Bespoke Software' }
      ]
    }
  }
}
</script>

<style scoped>
.page-header {
  padding: 160px 0 80px;
  background: linear-gradient(160deg, var(--navy-dark) 0%, var(--navy) 100%);
}

.page-header h1 {
  margin-bottom: 1rem;
}

.page-header-sub {
  color: var(--silver);
  font-size: 1.15rem;
  max-width: 520px;
}

.portfolio-card {
  background: var(--navy-medium);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 2.5rem;
  height: 100%;
  transition: all 0.3s ease;
}

.portfolio-card:hover {
  border-color: var(--accent);
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}

.portfolio-card-header {
  margin-bottom: 1rem;
}

.portfolio-card-header h3 {
  font-family: var(--font-heading);
  font-size: 1.5rem;
}

.portfolio-description {
  color: var(--silver);
  font-size: 0.95rem;
  line-height: 1.7;
  margin-bottom: 1.25rem;
}

.portfolio-highlights {
  list-style: none;
  padding: 0;
  margin: 0 0 1.25rem;
  border-top: 1px solid var(--border-subtle);
  padding-top: 1rem;
}

.portfolio-highlights li {
  color: var(--silver-light);
  font-size: 0.875rem;
  padding: 0.4rem 0;
  padding-left: 1.25rem;
  position: relative;
}

.portfolio-highlights li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 6px;
  height: 6px;
  border: 1.5px solid var(--accent);
  border-radius: 50%;
  transform: translateY(-50%);
}

.portfolio-levels {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  border-top: 1px solid var(--border-subtle);
  padding-top: 1.25rem;
}

.level-badge {
  display: inline-block;
  background: rgba(74, 124, 201, 0.12);
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 3px;
  letter-spacing: 0.5px;
}

.platform-card {
  background: var(--navy-medium);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 2rem 1rem;
  transition: all 0.3s ease;
}

.platform-card:hover {
  border-color: var(--accent);
}

.platform-name {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--white);
  margin-bottom: 0.35rem;
}

.platform-type {
  color: var(--silver);
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}
</style>
